import React from "react";

const TablePaginationComponents = ({
  columns = [],
  data = [],
  loading = false,
  pagination = {}, // <-- backend pagination object
  onPageChange,
  onRowsPerPageChange,
  renderActions,
}) => {
  const {
    total_records = 0,
    per_page = 10,
    current_page = 1,
    last_page = 1,
    previous_page = null,
    next_page = null,
  } = pagination;

  /** START–END Calculation */
  const start =
    total_records > 0 ? (current_page - 1) * per_page + 1 : 0;

  const end = Math.min(current_page * per_page, total_records);

  /** Generate Page Numbers */
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (last_page <= maxVisible) {
      for (let i = 1; i <= last_page; i++) pages.push(i);
    } else {
      let start = Math.max(1, current_page - 2);
      let end = Math.min(last_page, start + maxVisible - 1);

      if (end - start + 1 < maxVisible)
        start = Math.max(1, end - maxVisible + 1);

      if (start > 1) {
        pages.push(1);
        if (start > 2) pages.push("...");
      }

      for (let i = start; i <= end; i++) pages.push(i);

      if (end < last_page) {
        if (end < last_page - 1) pages.push("...");
        pages.push(last_page);
      }
    }

    return pages;
  };

  return (
    <>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>Records</h4>

        <div className="d-flex align-items-center gap-2">
          <span>Show:</span>
          <select
            className="form-select form-select-sm"
            style={{ width: "80px" }}
            value={per_page}
            onChange={(e) => onRowsPerPageChange(parseInt(e.target.value))}
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <span>entries</span>
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-striped table-hover">
          <thead>
            <tr>
              {columns.map((col, i) => (
                <th key={i}>{col.label}</th>
              ))}
              {renderActions && <th>Actions</th>}
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length + 1} className="text-center">
                  Loading...
                </td>
              </tr>
            ) : data.length > 0 ? (
              data.map((row, i) => (
                <tr key={i}>
                  {columns.map((col, cIndex) => (
                    <td key={cIndex}>
                      {col.render ? col.render(row, i) : row[col.key]}
                    </td>
                  ))}
                  {renderActions && <td>{renderActions(row)}</td>}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length + 1} className="text-center">
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {last_page > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <span>
            Showing {start} to {end} of {total_records} entries
          </span>

          <div className="d-flex align-items-center gap-1">
            {/* Prev Button */}
            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={!previous_page}
              onClick={() => onPageChange(previous_page)}
            >
              Prev
            </button>

            {/* Page numbers */}
            {getPageNumbers().map((p, idx) =>
              p === "..." ? (
                <span key={idx} className="px-2">
                  ...
                </span>
              ) : (
                <button
                  key={idx}
                  className={`btn btn-sm ${
                    p === current_page
                      ? "btn-primary text-white"
                      : "btn-light"
                  }`}
                  onClick={() => onPageChange(p)}
                >
                  {p}
                </button>
              )
            )}

            {/* Next Button */}
            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={!next_page}
              onClick={() => onPageChange(next_page)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default TablePaginationComponents;
